import type { ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';

export interface B2BHeroProps {
    headline: string;
    backgroundSrc: string;
    isVideo?: boolean;
    posterSrc?: string;
    scrollCueLabel?: string;
    /** Optional stylised "A" mark (Figma 461:24587) below the top nav, left. */
    logomarkSrc?: string;
    /** Transparent nav overlaid at the top of the hero. */
    topNav?: ReactNode;
    className?: string;
}

/**
 * Full-bleed hero (Figma node 443:24089, 1920×1229). Built to the Figma
 * coordinate system: the headline is centred (TWK Ghost, ~56.6px → 2.95vw,
 * #f9f9f7, line-height 0.94, letter-spacing −0.02em) at ~46% down, the nav sits
 * in a top gradient band, and the scroll cue is centred at the bottom. Sizes use
 * vw so the layout overlays the design proportionally at the 1920 reference width.
 */
export const B2BHero = ({
    headline,
    backgroundSrc,
    isVideo,
    posterSrc,
    scrollCueLabel,
    logomarkSrc,
    topNav,
    className,
}: B2BHeroProps) => {
    return (
        <section
            className={cn(
                'relative w-full overflow-hidden text-[#f9f9f7]',
                // Desktop: fill the viewport height so the scroll cue (at
                // top-[89%]) lands at the bottom of the viewport on load — the
                // Figma frame is 1920×1229 (aspect 1.56), which is taller than
                // most desktop viewports and pushed the cue below the fold.
                // The media is object-cover, so it just re-crops. Mobile (Figma
                // `521:26870`) keeps the 768×1229 portrait aspect (cue hidden).
                'max-md:aspect-[768/1229] md:h-[100svh]',
                className,
            )}
        >
            {isVideo ? (
                <video
                    className="absolute inset-0 size-full object-cover"
                    src={backgroundSrc}
                    poster={posterSrc}
                    autoPlay
                    muted
                    loop
                    playsInline
                />
            ) : (
                <img
                    className="absolute inset-0 size-full object-cover"
                    src={backgroundSrc}
                    alt=""
                />
            )}
            {/* Flat 20% black wash (matches the Figma image overlay). */}
            <div className="absolute inset-0 bg-black/20" />
            {/* Top gradient band for nav legibility (Figma 378px ≈ 30.7%). */}
            <div className="absolute inset-x-0 top-0 h-[30.7%] bg-gradient-to-b from-black/40 to-transparent" />

            {topNav}

            {/* Stylised "A" logomark (Figma 461:24587).
             *  - Desktop (≥md): 1920×1229 frame coords (50.21, 210),
             *    219.58×166 in vw.
             *  - Mobile (Figma `521:26870`, 768×1229): below the top nav at
             *    (28, 102), 220×166 — kept proportional via `aspect-[220/166]`
             *    and a `w-[28.6vw]` (= 220/768 × 100) sizing.
             *  SVG is white-fill so it only shows on dark photo + gradient. */}
            {logomarkSrc && (
                <img
                    src={logomarkSrc}
                    alt=""
                    aria-hidden
                    data-b2b-hero-logomark
                    className={cn(
                        'absolute',
                        'md:left-[2.615vw] md:top-[10.937vw] md:h-[8.646vw] md:w-[11.437vw]',
                        'max-md:left-[3.65vw] max-md:top-[13.28vw] max-md:aspect-[220/166] max-md:w-[28.6vw]',
                    )}
                />
            )}

            {/* Headline.
             *  - Desktop: centred at ~46.25% down, ~50% frame width.
             *  - Mobile (Figma `521:26870`): (28.84, 648.58, w=707.56, h=120)
             *    in the 768×1229 frame. 28.84/768 ≈ 3.755 % left,
             *    648.58/1229 ≈ 52.77 % top, 707.56/768 ≈ 92.13 % width.
             *    Font is TWK Ghost 42.539 px = 5.539 vw (proportional to the
             *    768 frame), leading 0.94, tracking -0.02em. Stays left-aligned
             *    per direct user direction. */}
            <h1
                className={cn(
                    'absolute',
                    'md:left-1/2 md:top-[46.25%] md:w-[50.2%] md:-translate-x-1/2 md:text-center',
                    'md:font-twk-ghost md:text-[2.95vw] md:leading-[0.94] md:tracking-[-0.02em]',
                    'max-md:left-[3.755%] max-md:top-[52.77%] max-md:w-[92.13%] max-md:text-center',
                    'max-md:font-twk-ghost max-md:text-[5.539vw] max-md:leading-[0.94] max-md:tracking-[-0.02em]',
                )}
            >
                {headline}
            </h1>

            {scrollCueLabel && (
                <button
                    type="button"
                    onClick={(e) => {
                        // Smooth-scroll to the hero's next sibling — that's
                        // the first content section under the hero. Falls
                        // back to one viewport down if there's no sibling.
                        const hero =
                            e.currentTarget.closest('section') ??
                            e.currentTarget.closest('[data-b2b-hero]');
                        const next =
                            hero?.nextElementSibling as HTMLElement | null;
                        if (next) {
                            next.scrollIntoView({ behavior: 'smooth' });
                        } else {
                            window.scrollBy({
                                top: window.innerHeight,
                                behavior: 'smooth',
                            });
                        }
                    }}
                    aria-label={`Scroll to next section (${scrollCueLabel})`}
                    // Hidden on mobile — the long hero on a phone already
                    // implies scrollability; the cue takes valuable space.
                    className="absolute left-1/2 top-[89%] flex -translate-x-1/2 cursor-pointer flex-col items-center gap-[0.6vw] transition-opacity hover:opacity-70 max-md:hidden"
                >
                    <span className="font-twk-ghost text-[1.3vw] leading-none max-md:text-base">
                        {scrollCueLabel}
                    </span>
                    {/* Down arrow — vertical line with arrowhead at the
                     * bottom. Wider/taller than the previous chevron-only
                     * icon so the indicator reads as an arrow ↓. */}
                    <svg
                        aria-hidden
                        viewBox="0 0 14 22"
                        fill="none"
                        className="h-[1.4vw] w-[0.9vw] animate-bounce max-md:h-6 max-md:w-4"
                    >
                        <path
                            d="M7 1 V21 M1 15 L7 21 L13 15"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            )}
        </section>
    );
};
